import React from 'react';
import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors, useDraggable, useDroppable, closestCorners } from '@dnd-kit/core';
import { useT, fmtMoney } from '../salesI18n';
import { STATUS_ORDER, STATUS_STYLES } from './StatusBadge';
import { LeadCard } from './LeadCard';

const DraggableLead = ({ lead }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: lead.id, data: { lead } });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes} className={`touch-none ${isDragging ? 'opacity-70 z-50 relative' : ''}`} data-testid={`kanban-card-${lead.id}`}>
      <LeadCard lead={lead} compact showStatus={false} />
    </div>
  );
};

const Column = ({ status, leads }) => {
  const { t, lang } = useT();
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const st = STATUS_STYLES[status];
  const total = leads.reduce((s, l) => s + (Number(l.estimated_value) || 0), 0);
  return (
    <div ref={setNodeRef} data-testid={`kanban-column-${status}`} className={`w-[280px] shrink-0 rounded-3xl border bg-white/50 backdrop-blur-xl shadow-sm ${isOver ? 'ring-2 ring-teal-500/60 border-teal-200 bg-white/75' : 'border-white/80'} flex flex-col max-h-[calc(100dvh-240px)] transition-all duration-200`}>
      <div className="px-4 py-3 border-b border-white/80">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-2 font-semibold text-sm text-slate-800"><span className={`h-2.5 w-2.5 rounded-full ${st.dot}`} /> {t.status[status]}</span>
          <span className="text-xs font-semibold text-slate-700 rounded-full bg-white/80 border border-white px-2 py-0.5 tabular-nums" data-testid={`kanban-count-${status}`}>{leads.length}</span>
        </div>
        <p className="text-[11px] text-slate-500 mt-0.5 tabular-nums">{fmtMoney(total, lang)}</p>
      </div>
      <div className="p-2 space-y-2 overflow-y-auto sales-kanban-scroll">
        {leads.map((l) => <DraggableLead key={l.id} lead={l} />)}
        {leads.length === 0 && <div className="text-[11px] text-slate-400 text-center py-6 rounded-2xl border border-dashed border-slate-300/70">—</div>}
      </div>
    </div>
  );
};

export const LeadKanban = ({ leads, onMove }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
  );
  const onDragEnd = ({ active, over }) => {
    if (!over) return;
    const lead = active.data.current?.lead;
    if (lead && over.id !== lead.status) onMove(lead, over.id);
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={onDragEnd}>
      <div className="flex gap-3 overflow-x-auto sales-kanban-scroll pb-3 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="lead-kanban">
        {STATUS_ORDER.map((s) => <Column key={s} status={s} leads={leads.filter((l) => l.status === s)} />)}
      </div>
    </DndContext>
  );
};
